import { useState, useEffect } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { Link, useLocation } from 'react-router-dom';
import { useAccountCookiePreferences, useSyncAccountCookiePreferences, saveAccountCookiePreferences } from '../hooks/useAccountCookiePreferences';
import CookiePreferenceForm from './CookiePreferenceForm';
import './cookieConsent.css';

export default function CookiePolicyFloatingButton() {
    useSyncAccountCookiePreferences();
    const state = useAccountCookiePreferences();
    const location = useLocation();
    const [panel, setPanel] = useState<'banner' | 'preferences' | null>(null);

    // Routes where transactional actions take place and cookie modals must not intrude
    const isExemptPath = [
        '/verify-email',
        '/promotii/confirmare-email',
        '/complete-profile',
        '/reset-password',
        '/editor-document',
    ].some(path => location.pathname.startsWith(path));

    useEffect(() => {
        if (state.loading) return;
        if (isExemptPath) {
            setPanel(p => (p === 'banner' ? null : p));
            return;
        }
        if (state.needsReconsent) {
            setPanel(p => (p === null ? 'banner' : p));
        } else {
            setPanel(p => (p === 'banner' ? null : p));
        }
    }, [state.loading, state.needsReconsent, state.userId, isExemptPath]);

    const close = async () => {
        const wasBanner = panel === 'banner';
        setPanel(null);
        if (wasBanner && state.needsReconsent) {
            await saveAccountCookiePreferences(false, false, 'banner');
        }
    };

    const choose = async (value: boolean) => {
        if (await saveAccountCookiePreferences(value, value, 'banner')) {
            setPanel(null);
        } else {
            setPanel(null);
        }
    };

    if (location.pathname.startsWith('/editor-document')) {
        return null;
    }

    return <>
        {!panel && <button type="button" className="cookie-reopen" onClick={() => setPanel('preferences')} aria-haspopup="dialog">Preferințe cookie</button>}
        <Dialog open={!!panel && !state.loading} onClose={close} className={`cookie-dialog ${panel === 'banner' ? 'cookie-notice-dialog' : ''}`}>
            <div className="cookie-backdrop" aria-hidden="true" />
            <div className="cookie-position">
                <DialogPanel className={panel === 'banner' ? 'cookie-notice' : 'cookie-preferences'}>
                    {panel === 'banner' ? <>
                        <button type="button" className="cookie-close" aria-label="Închide fără a accepta" onClick={close}>×</button>
                        <div className="cookie-notice-copy"><DialogTitle>Folosim module cookie</DialogTitle>
                            <p>Modulele cookie ajută site-ul să funcționeze, să măsoare utilizarea și să susțină marketingul. <button className="cookie-text-link" onClick={() => setPanel('preferences')}>Gestionează</button> preferințele modulelor cookie oricând. Află mai multe despre <Link to="/cookies" onClick={close}>politica noastră privind modulele cookie</Link>.</p></div>
                        <div className="cookie-actions"><button className="cookie-manage" onClick={() => setPanel('preferences')}>Preferințe cookie</button>
                            <button className="cookie-secondary" onClick={() => void choose(false)} disabled={state.saving}>Respinge elementele neesențiale</button>
                            <button className="cookie-primary" onClick={() => void choose(true)} disabled={state.saving}>Acceptă toate</button></div>
                        {state.error && <p role="alert" className="cookie-error">{state.error}</p>}
                    </> : <>
                        <button type="button" className="cookie-back" onClick={close}>Înapoi la LegeaAplicata</button>
                        <DialogTitle>Preferințe cookie</DialogTitle>
                        <CookiePreferenceForm source="banner" onSaved={close} />
                    </>}
                </DialogPanel>
            </div>
        </Dialog>
    </>;
}
